/* ==========================================================================
   FREE GIFTS — AUTO-ADD TO CART (metafield gifts + threshold tier gifts)
   File: assets/free-gifts.js

   Metafield gifts: reads cart items' `product.metafields.custom.free_gifts`
   via the inline <script id="FreeGiftsData"> rendered by cart-drawer.liquid.
   Tier gifts: reads thresholds/variants from <script id="TierGiftsData">
   (theme settings) and grants/removes gifts as the paid subtotal crosses
   each threshold.

   PERFORMANCE: after any /cart/add|change|update this reuses the cart JSON
   from the mutation response when possible (change/update return the full
   cart), computes ALL gift changes from one snapshot, then applies them in
   at most two requests: one batched /cart/add.js for every missing gift and
   one /cart/update.js for every removal. The drawer re-render runs in
   parallel with the computation, with a second render only if gifts changed.
   ========================================================================== */

(function () {
  const PROPERTY_KEY = '_free_gift';
  const TRIGGER_KEY = '_gift_for';
  const TIER_KEY = '_tier_gift';

  /* ---- COMBO GIFT SUPERSEDES SINGLES ----
     When the combined "Nicotine Detox eBook & Habit Tracker" is in the cart,
     the separate "Nicotine Detox eBook" and "Habit Tracker" gifts are not
     added, and any FREE ($0) lines of them already in the cart are removed.
     Matched by product title (case-insensitive). If you rename these products,
     update the titles here. Paid lines of the singles are never touched. */
  const COMBO_GIFT_TITLES = ['nicotine detox ebook & habit tracker'];
  const SUPERSEDED_GIFT_TITLES = ['nicotine detox ebook', 'habit tracker'];

  function normTitle(t) {
    return String(t || '').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim().toLowerCase();
  }
  function cartHasComboGift(cart) {
    return cart.items.some((i) => COMBO_GIFT_TITLES.includes(normTitle(i.product_title)) ||
      (i.properties && i.properties._bundle_combo)); // Reset Stack bundle: selected eBook & Habit Tracker gift
  }
  function isSupersededTitle(t) {
    return SUPERSEDED_GIFT_TITLES.includes(normTitle(t));
  }

  /* ---- LOOP GUARD ----
     If this script performs the SAME gift add/remove 3+ times inside a minute,
     another script (a cart app such as Rebuy Smart Cart, a bundle app, etc.)
     is reverting our changes and we are in a mutation loop. Fighting it would
     hammer Shopify's cart API until the visitor's IP gets rate-limited (429s
     storewide), so instead we stand down for 5 minutes and say so loudly. */
  const recentOps = new Map(); // opKey -> timestamps within the last 60s
  let standDownUntil = 0;      // loop detected
  let throttledUntil = 0;      // Shopify returned persistent 429s

  function loopGuard(opKey) {
    const now = Date.now();
    const hits = (recentOps.get(opKey) || []).filter((t) => now - t < 60000);
    hits.push(now);
    recentOps.set(opKey, hits);
    if (hits.length >= 3) {
      standDownUntil = now + 5 * 60 * 1000;
      console.error('[FreeGifts] LOOP GUARD: "' + opKey + '" attempted ' + hits.length +
        'x in 60s — another script keeps reverting gift changes (check cart-managing apps like Rebuy Smart Cart). ' +
        'Pausing gift sync for 5 minutes to protect the store from rate limiting.');
      return false;
    }
    return true;
  }

  /* ---------------- config readers ---------------- */

  function readGiftConfig() {
    const node = document.getElementById('FreeGiftsData');
    if (!node) return { gifts: [] };
    try { return JSON.parse(node.textContent || '{}'); }
    catch (e) { console.warn('[FreeGifts] Bad JSON in #FreeGiftsData', e); return { gifts: [] }; }
  }

  function readTierConfig() {
    const node = document.getElementById('TierGiftsData');
    if (!node) return { insuranceHandle: null, tiers: [] };
    try {
      const cfg = JSON.parse(node.textContent || '{}');
      return { insuranceHandle: cfg.insuranceHandle || null, currency: cfg.currency || null, tiers: Array.isArray(cfg.tiers) ? cfg.tiers : [] };
    } catch (e) {
      console.warn('[TierGifts] Bad JSON in #TierGiftsData', e);
      return { insuranceHandle: null, tiers: [] };
    }
  }

  /* ---------------- cart helpers ---------------- */

  // Fetch JSON with retry on 429/5xx (Shopify throttles /cart/*.js — hard on
  // shopifypreview.com, possible on production under load). Honors Retry-After.
  async function fetchJsonRetry(url, init, retries) {
    retries = typeof retries === 'number' ? retries : 2;
    for (let attempt = 0; ; attempt++) {
      const res = await fetch(url, init);
      if (res.ok) {
        try { return await res.json(); }
        catch (e) { console.warn('[FreeGifts] Non-JSON response from', url); return null; }
      }
      if ((res.status === 429 || res.status >= 500) && attempt < retries) {
        const retryAfter = parseFloat(res.headers.get('Retry-After')) || 0;
        const waitMs = Math.max(retryAfter * 1000, 700 * (attempt + 1));
        console.warn('[FreeGifts]', url, 'returned', res.status, '— retrying in', waitMs, 'ms');
        await new Promise((r) => setTimeout(r, waitMs));
        continue;
      }
      if (res.status === 429) {
        throttledUntil = Date.now() + 30000;
        console.warn('[FreeGifts]', url, 'still 429 after retries — pausing gift sync for 30s');
      } else {
        console.warn('[FreeGifts]', url, 'failed with status', res.status);
      }
      return null;
    }
  }

  async function fetchCart() {
    return fetchJsonRetry('/cart.js', { headers: { Accept: 'application/json' } });
  }

  // change.js / update.js respond with the FULL cart — reuse it and skip a round-trip.
  async function cartFromMutation(url, responseClone) {
    if (!responseClone) return null;
    if (!/\/cart\/(change|update)(\.js)?(\?|$)/.test(url)) return null;
    try {
      const j = await responseClone.json();
      if (j && Array.isArray(j.items) && typeof j.items_subtotal_price !== 'undefined') return j;
    } catch (e) { /* fall through to /cart.js */ }
    return null;
  }

  async function addItemsBatch(items) {
    return fetchJsonRetry('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
  }

  async function removeItemsBatch(updates) {
    return fetchJsonRetry('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
  }

  /* ---------------- gift computation (pure, from one snapshot) ---------------- */

  function computeExpectedMetafieldGifts(cart, config) {
    const expected = new Map(); // gift variantId -> trigger product id
    const comboInCart = cartHasComboGift(cart);
    cart.items.forEach((item) => {
      if (item.properties && (item.properties[PROPERTY_KEY] || item.properties[TIER_KEY])) return;
      if (item.properties && item.properties._bundle_own_gifts) return; // Reset Stack bundle adds its own gifts for this line
      (config.gifts || []).forEach((g) => {
        if (comboInCart && isSupersededTitle(g.gift_title)) return; // combo gift covers it
        if (g.trigger_product_id === item.product_id || g.trigger_variant_id === item.variant_id) {
          if (!expected.has(g.gift_variant_id)) expected.set(g.gift_variant_id, item.product_id);
        }
      });
    });
    return expected;
  }

  function rewardsSubtotal(cart, insuranceHandle) {
    // Same definition as the Liquid `rewards_subtotal`: paid items only.
    return cart.items.reduce((sum, item) => {
      const props = item.properties || {};
      if (props[PROPERTY_KEY] || props[TIER_KEY]) return sum;
      if (insuranceHandle && item.handle === insuranceHandle) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function computeExpectedTierGifts(cart, tierConfig) {
    const expected = new Map(); // variantId -> tier number
    if (!tierConfig.tiers.length) return expected;
    const subtotal = rewardsSubtotal(cart, tierConfig.insuranceHandle);
    tierConfig.tiers.forEach((t) => {
      if (t.variantId && subtotal >= t.amount) expected.set(t.variantId, t.tier);
    });
    return expected;
  }

  /* ----------------------------------------------------------------------
     syncGifts — the single reconciliation pass.
     Computes metafield + tier gifts from one cart snapshot, then applies
     every change in at most TWO requests (one batched add, one batched
     removal). Returns true if the cart was mutated.
     ---------------------------------------------------------------------- */
  async function syncGifts(cart) {
    const now = Date.now();
    if (now < standDownUntil || now < throttledUntil) return false;
    if (!cart) {
      try { cart = await fetchCart(); }
      catch (e) { console.warn('[FreeGifts] cart fetch failed', e); return false; }
    }
    if (!cart || !Array.isArray(cart.items)) return false;

    // MULTI-CURRENCY GUARD: the tier thresholds in #TierGiftsData were resolved
    // for a specific presentment currency. If the live cart is in a different
    // currency (shopper just switched markets and the drawer hasn't re-rendered
    // yet), comparing them would grant/revoke gifts wrongly — skip this pass;
    // the next render carries matching thresholds.
    const tierCfgForCurrency = readTierConfig();
    if (tierCfgForCurrency.currency && cart.currency && tierCfgForCurrency.currency !== cart.currency) {
      console.warn('[FreeGifts] currency mismatch (config', tierCfgForCurrency.currency, 'vs cart', cart.currency, ') — skipping gift sync until re-render');
      return false;
    }

    const expectedMeta = computeExpectedMetafieldGifts(cart, readGiftConfig());
    const expectedTier = computeExpectedTierGifts(cart, readTierConfig());

    const presentMeta = new Map(); // variantId -> key
    const presentTier = new Map(); // variantId -> key
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (props[TIER_KEY]) presentTier.set(item.variant_id, item.key);
      else if (props[PROPERTY_KEY]) presentMeta.set(item.variant_id, item.key);
    });

    const adds = [];
    const removals = {};

    for (const [variantId, triggerProductId] of expectedMeta.entries()) {
      if (!presentMeta.has(variantId)) {
        adds.push({ id: variantId, quantity: 1, properties: { [PROPERTY_KEY]: 'true', [TRIGGER_KEY]: String(triggerProductId) } });
      }
    }
    for (const [variantId, key] of presentMeta.entries()) {
      if (!expectedMeta.has(variantId)) removals[key] = 0;
    }
    for (const [variantId, tier] of expectedTier.entries()) {
      if (!presentTier.has(variantId)) {
        adds.push({ id: variantId, quantity: 1, properties: { [TIER_KEY]: String(tier) } });
      }
    }
    const removalVariantIds = [];
    for (const [variantId, key] of presentTier.entries()) {
      if (!expectedTier.has(variantId)) { removals[key] = 0; removalVariantIds.push(variantId); }
    }
    // Combo gift in cart -> drop any FREE lines of the separate eBook / Habit Tracker,
    // whichever script or app added them (metafield gift lines are already covered above).
    if (cartHasComboGift(cart)) {
      cart.items.forEach((item) => {
        if (isSupersededTitle(item.product_title) && item.final_line_price === 0) {
          removals[item.key] = 0;
          removalVariantIds.push(item.variant_id);
        }
      });
    }

    // Loop guard: drop any operation we've already performed repeatedly.
    const guardedAdds = adds.filter((a) => loopGuard('add:' + a.id));
    const removalEntries = Object.keys(removals).map((key) => key);
    // (removal keys change per line instance, so guard removals by variant)
    const keyToVariant = {};
    cart.items.forEach((i) => { keyToVariant[i.key] = i.variant_id; });
    const guardedRemovals = {};
    removalEntries.forEach((key) => {
      if (loopGuard('remove:' + (keyToVariant[key] || key))) guardedRemovals[key] = 0;
    });

    if (!guardedAdds.length && !Object.keys(guardedRemovals).length) return false;

    // OPTIMISTIC REMOVAL: hide de-qualified gift rows in the DOM immediately —
    // the customer sees the gift disappear the instant the threshold is lost,
    // while the real /cart/update.js + re-render catch up in the background.
    removalVariantIds.forEach((vid) => {
      document.querySelectorAll('[data-gift-variant="' + vid + '"]').forEach((el) => { el.style.display = 'none'; });
    });

    console.log('[FreeGifts] Syncing gifts. Adds:', guardedAdds.map(a => a.id), 'Removals:', Object.keys(guardedRemovals));
    if (guardedAdds.length) {
      try { await addItemsBatch(guardedAdds); }
      catch (e) { console.warn('[FreeGifts] batched add failed', e); }
    }
    if (Object.keys(guardedRemovals).length) {
      try { await removeItemsBatch(guardedRemovals); }
      catch (e) { console.warn('[FreeGifts] batched removal failed', e); }
    }
    return true;
  }

  /* ---------------- drawer re-render ---------------- */

  async function rerenderDrawer() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      if (!res.ok) { console.warn('[FreeGifts] section render returned', res.status, '— skipping this render'); return; }
      const data = await res.json();
      const parser = new DOMParser();

      if (data['cart-drawer']) {
        const newDoc = parser.parseFromString(data['cart-drawer'], 'text/html');
        const newDrawer = newDoc.querySelector('cart-drawer');
        const oldDrawer = document.querySelector('cart-drawer');
        if (newDrawer && oldDrawer) {
          const wasOpen = oldDrawer.classList.contains('active');
          oldDrawer.innerHTML = newDrawer.innerHTML;
          const overlay = oldDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', () => oldDrawer.close && oldDrawer.close());
          if (wasOpen) oldDrawer.classList.add('active');
        }
      }
      if (data['cart-icon-bubble']) {
        const newDoc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        const newBubble = newDoc.querySelector('#shopify-section-cart-icon-bubble') || newDoc.querySelector('.shopify-section');
        const oldBubble = document.querySelector('#shopify-section-cart-icon-bubble');
        if (newBubble && oldBubble) oldBubble.innerHTML = newBubble.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) { console.warn('[FreeGifts] rerender failed', e); }
  }

  /* ---------------- fetch hook ---------------- */

  function installFetchHook() {
    const origFetch = window.fetch;
    let reconciling = false;

    window.fetch = async function (...args) {
      const response = await origFetch.apply(this, args);
      try {
        const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
        const isCartMutation = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
        if (isCartMutation && !reconciling) {
          const init = args[1] || {};
          const body = init.body || '';
          const isInternal = (typeof body === 'string' && (body.includes('"_free_gift"') || body.includes('"_tier_gift"')));
          if (!isInternal) {
            reconciling = true;
            const responseClone = response.clone();
            // Theme components (quantity steppers, product forms) bundle a
            // `sections` render into their own mutation and update the drawer
            // themselves — our extra render would be a wasted request. Only
            // render for sources that DON'T (third-party apps, raw calls).
            const sourceRendersSections = (typeof body === 'string' && body.includes('"sections"'));
            setTimeout(async () => {
              try {
                // 1) Reflect the customer's action right away — in PARALLEL
                //    with the gift computation below — but only when the
                //    mutation source didn't already render the drawer.
                const firstRender = sourceRendersSections ? Promise.resolve() : rerenderDrawer();
                // 2) Get the cart WITHOUT an extra round-trip when possible.
                const cart = (await cartFromMutation(url, responseClone)) || (await fetchCart());
                // 3) One reconciliation pass, max two batched requests.
                const changed = await syncGifts(cart);
                await firstRender;
                // 4) Gifts changed → one more render so they appear.
                if (changed) await rerenderDrawer();
              } catch (e) { console.warn('[FreeGifts] reconcile sequence failed', e); }
              reconciling = false;
            }, 10);
          }
        }
      } catch (e) { /* never break the original fetch */ }
      return response;
    };
  }

  /* ---------------- init ---------------- */

  async function init() {
    installFetchHook();
    try {
      // Sync on load: carts built on a previous visit, or thresholds changed
      // in the customizer, get corrected immediately.
      const changed = await syncGifts(null);
      if (changed) await rerenderDrawer();
    } catch (e) { console.warn('[FreeGifts] init reconcile failed', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// /* ==========================================================================
//    FREE GIFTS — AUTO-ADD TO CART (metafield gifts + threshold tier gifts)
//    File: assets/free-gifts.js

//    Metafield gifts: reads cart items' `product.metafields.custom.free_gifts`
//    via the inline <script id="FreeGiftsData"> rendered by cart-drawer.liquid.
//    Tier gifts: reads thresholds/variants from <script id="TierGiftsData">
//    (theme settings) and grants/removes gifts as the paid subtotal crosses
//    each threshold.

//    PERFORMANCE: after any /cart/add|change|update this reuses the cart JSON
//    from the mutation response when possible (change/update return the full
//    cart), computes ALL gift changes from one snapshot, then applies them in
//    at most two requests: one batched /cart/add.js for every missing gift and
//    one /cart/update.js for every removal. The drawer re-render runs in
//    parallel with the computation, with a second render only if gifts changed.
//    ========================================================================== */

// (function () {
//   const PROPERTY_KEY = '_free_gift';
//   const TRIGGER_KEY = '_gift_for';
//   const TIER_KEY = '_tier_gift';

//   /* ---- LOOP GUARD ----
//      If this script performs the SAME gift add/remove 3+ times inside a minute,
//      another script (a cart app such as Rebuy Smart Cart, a bundle app, etc.)
//      is reverting our changes and we are in a mutation loop. Fighting it would
//      hammer Shopify's cart API until the visitor's IP gets rate-limited (429s
//      storewide), so instead we stand down for 5 minutes and say so loudly. */
//   const recentOps = new Map(); // opKey -> timestamps within the last 60s
//   let standDownUntil = 0;      // loop detected
//   let throttledUntil = 0;      // Shopify returned persistent 429s

//   function loopGuard(opKey) {
//     const now = Date.now();
//     const hits = (recentOps.get(opKey) || []).filter((t) => now - t < 60000);
//     hits.push(now);
//     recentOps.set(opKey, hits);
//     if (hits.length >= 3) {
//       standDownUntil = now + 5 * 60 * 1000;
//       console.error('[FreeGifts] LOOP GUARD: "' + opKey + '" attempted ' + hits.length +
//         'x in 60s — another script keeps reverting gift changes (check cart-managing apps like Rebuy Smart Cart). ' +
//         'Pausing gift sync for 5 minutes to protect the store from rate limiting.');
//       return false;
//     }
//     return true;
//   }

//   /* ---------------- config readers ---------------- */

//   function readGiftConfig() {
//     const node = document.getElementById('FreeGiftsData');
//     if (!node) return { gifts: [] };
//     try { return JSON.parse(node.textContent || '{}'); }
//     catch (e) { console.warn('[FreeGifts] Bad JSON in #FreeGiftsData', e); return { gifts: [] }; }
//   }

//   function readTierConfig() {
//     const node = document.getElementById('TierGiftsData');
//     if (!node) return { insuranceHandle: null, tiers: [] };
//     try {
//       const cfg = JSON.parse(node.textContent || '{}');
//       return { insuranceHandle: cfg.insuranceHandle || null, currency: cfg.currency || null, tiers: Array.isArray(cfg.tiers) ? cfg.tiers : [] };
//     } catch (e) {
//       console.warn('[TierGifts] Bad JSON in #TierGiftsData', e);
//       return { insuranceHandle: null, tiers: [] };
//     }
//   }

//   /* ---------------- cart helpers ---------------- */

//   // Fetch JSON with retry on 429/5xx (Shopify throttles /cart/*.js — hard on
//   // shopifypreview.com, possible on production under load). Honors Retry-After.
//   async function fetchJsonRetry(url, init, retries) {
//     retries = typeof retries === 'number' ? retries : 2;
//     for (let attempt = 0; ; attempt++) {
//       const res = await fetch(url, init);
//       if (res.ok) {
//         try { return await res.json(); }
//         catch (e) { console.warn('[FreeGifts] Non-JSON response from', url); return null; }
//       }
//       if ((res.status === 429 || res.status >= 500) && attempt < retries) {
//         const retryAfter = parseFloat(res.headers.get('Retry-After')) || 0;
//         const waitMs = Math.max(retryAfter * 1000, 700 * (attempt + 1));
//         console.warn('[FreeGifts]', url, 'returned', res.status, '— retrying in', waitMs, 'ms');
//         await new Promise((r) => setTimeout(r, waitMs));
//         continue;
//       }
//       if (res.status === 429) {
//         throttledUntil = Date.now() + 30000;
//         console.warn('[FreeGifts]', url, 'still 429 after retries — pausing gift sync for 30s');
//       } else {
//         console.warn('[FreeGifts]', url, 'failed with status', res.status);
//       }
//       return null;
//     }
//   }

//   async function fetchCart() {
//     return fetchJsonRetry('/cart.js', { headers: { Accept: 'application/json' } });
//   }

//   // change.js / update.js respond with the FULL cart — reuse it and skip a round-trip.
//   async function cartFromMutation(url, responseClone) {
//     if (!responseClone) return null;
//     if (!/\/cart\/(change|update)(\.js)?(\?|$)/.test(url)) return null;
//     try {
//       const j = await responseClone.json();
//       if (j && Array.isArray(j.items) && typeof j.items_subtotal_price !== 'undefined') return j;
//     } catch (e) { /* fall through to /cart.js */ }
//     return null;
//   }

//   async function addItemsBatch(items) {
//     return fetchJsonRetry('/cart/add.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//       body: JSON.stringify({ items })
//     });
//   }

//   async function removeItemsBatch(updates) {
//     return fetchJsonRetry('/cart/update.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//       body: JSON.stringify({ updates })
//     });
//   }

//   /* ---------------- gift computation (pure, from one snapshot) ---------------- */

//   function computeExpectedMetafieldGifts(cart, config) {
//     const expected = new Map(); // gift variantId -> trigger product id
//     cart.items.forEach((item) => {
//       if (item.properties && (item.properties[PROPERTY_KEY] || item.properties[TIER_KEY])) return;
//       (config.gifts || []).forEach((g) => {
//         if (g.trigger_product_id === item.product_id || g.trigger_variant_id === item.variant_id) {
//           if (!expected.has(g.gift_variant_id)) expected.set(g.gift_variant_id, item.product_id);
//         }
//       });
//     });
//     return expected;
//   }

//   function rewardsSubtotal(cart, insuranceHandle) {
//     // Same definition as the Liquid `rewards_subtotal`: paid items only.
//     return cart.items.reduce((sum, item) => {
//       const props = item.properties || {};
//       if (props[PROPERTY_KEY] || props[TIER_KEY]) return sum;
//       if (insuranceHandle && item.handle === insuranceHandle) return sum;
//       return sum + item.final_line_price;
//     }, 0);
//   }

//   function computeExpectedTierGifts(cart, tierConfig) {
//     const expected = new Map(); // variantId -> tier number
//     if (!tierConfig.tiers.length) return expected;
//     const subtotal = rewardsSubtotal(cart, tierConfig.insuranceHandle);
//     tierConfig.tiers.forEach((t) => {
//       if (t.variantId && subtotal >= t.amount) expected.set(t.variantId, t.tier);
//     });
//     return expected;
//   }

//   /* ----------------------------------------------------------------------
//      syncGifts — the single reconciliation pass.
//      Computes metafield + tier gifts from one cart snapshot, then applies
//      every change in at most TWO requests (one batched add, one batched
//      removal). Returns true if the cart was mutated.
//      ---------------------------------------------------------------------- */
//   async function syncGifts(cart) {
//     const now = Date.now();
//     if (now < standDownUntil || now < throttledUntil) return false;
//     if (!cart) {
//       try { cart = await fetchCart(); }
//       catch (e) { console.warn('[FreeGifts] cart fetch failed', e); return false; }
//     }
//     if (!cart || !Array.isArray(cart.items)) return false;

//     // MULTI-CURRENCY GUARD: the tier thresholds in #TierGiftsData were resolved
//     // for a specific presentment currency. If the live cart is in a different
//     // currency (shopper just switched markets and the drawer hasn't re-rendered
//     // yet), comparing them would grant/revoke gifts wrongly — skip this pass;
//     // the next render carries matching thresholds.
//     const tierCfgForCurrency = readTierConfig();
//     if (tierCfgForCurrency.currency && cart.currency && tierCfgForCurrency.currency !== cart.currency) {
//       console.warn('[FreeGifts] currency mismatch (config', tierCfgForCurrency.currency, 'vs cart', cart.currency, ') — skipping gift sync until re-render');
//       return false;
//     }

//     const expectedMeta = computeExpectedMetafieldGifts(cart, readGiftConfig());
//     const expectedTier = computeExpectedTierGifts(cart, readTierConfig());

//     const presentMeta = new Map(); // variantId -> key
//     const presentTier = new Map(); // variantId -> key
//     cart.items.forEach((item) => {
//       const props = item.properties || {};
//       if (props[TIER_KEY]) presentTier.set(item.variant_id, item.key);
//       else if (props[PROPERTY_KEY]) presentMeta.set(item.variant_id, item.key);
//     });

//     const adds = [];
//     const removals = {};

//     for (const [variantId, triggerProductId] of expectedMeta.entries()) {
//       if (!presentMeta.has(variantId)) {
//         adds.push({ id: variantId, quantity: 1, properties: { [PROPERTY_KEY]: 'true', [TRIGGER_KEY]: String(triggerProductId) } });
//       }
//     }
//     for (const [variantId, key] of presentMeta.entries()) {
//       if (!expectedMeta.has(variantId)) removals[key] = 0;
//     }
//     for (const [variantId, tier] of expectedTier.entries()) {
//       if (!presentTier.has(variantId)) {
//         adds.push({ id: variantId, quantity: 1, properties: { [TIER_KEY]: String(tier) } });
//       }
//     }
//     const removalVariantIds = [];
//     for (const [variantId, key] of presentTier.entries()) {
//       if (!expectedTier.has(variantId)) { removals[key] = 0; removalVariantIds.push(variantId); }
//     }

//     // Loop guard: drop any operation we've already performed repeatedly.
//     const guardedAdds = adds.filter((a) => loopGuard('add:' + a.id));
//     const removalEntries = Object.keys(removals).map((key) => key);
//     // (removal keys change per line instance, so guard removals by variant)
//     const keyToVariant = {};
//     cart.items.forEach((i) => { keyToVariant[i.key] = i.variant_id; });
//     const guardedRemovals = {};
//     removalEntries.forEach((key) => {
//       if (loopGuard('remove:' + (keyToVariant[key] || key))) guardedRemovals[key] = 0;
//     });

//     if (!guardedAdds.length && !Object.keys(guardedRemovals).length) return false;

//     // OPTIMISTIC REMOVAL: hide de-qualified gift rows in the DOM immediately —
//     // the customer sees the gift disappear the instant the threshold is lost,
//     // while the real /cart/update.js + re-render catch up in the background.
//     removalVariantIds.forEach((vid) => {
//       document.querySelectorAll('[data-gift-variant="' + vid + '"]').forEach((el) => { el.style.display = 'none'; });
//     });

//     console.log('[FreeGifts] Syncing gifts. Adds:', guardedAdds.map(a => a.id), 'Removals:', Object.keys(guardedRemovals));
//     if (guardedAdds.length) {
//       try { await addItemsBatch(guardedAdds); }
//       catch (e) { console.warn('[FreeGifts] batched add failed', e); }
//     }
//     if (Object.keys(guardedRemovals).length) {
//       try { await removeItemsBatch(guardedRemovals); }
//       catch (e) { console.warn('[FreeGifts] batched removal failed', e); }
//     }
//     return true;
//   }

//   /* ---------------- drawer re-render ---------------- */

//   async function rerenderDrawer() {
//     try {
//       const res = await fetch('/?sections=cart-drawer,cart-icon-bubble');
//       if (!res.ok) { console.warn('[FreeGifts] section render returned', res.status, '— skipping this render'); return; }
//       const data = await res.json();
//       const parser = new DOMParser();

//       if (data['cart-drawer']) {
//         const newDoc = parser.parseFromString(data['cart-drawer'], 'text/html');
//         const newDrawer = newDoc.querySelector('cart-drawer');
//         const oldDrawer = document.querySelector('cart-drawer');
//         if (newDrawer && oldDrawer) {
//           const wasOpen = oldDrawer.classList.contains('active');
//           oldDrawer.innerHTML = newDrawer.innerHTML;
//           const overlay = oldDrawer.querySelector('#CartDrawer-Overlay');
//           if (overlay) overlay.addEventListener('click', () => oldDrawer.close && oldDrawer.close());
//           if (wasOpen) oldDrawer.classList.add('active');
//         }
//       }
//       if (data['cart-icon-bubble']) {
//         const newDoc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
//         const newBubble = newDoc.querySelector('#shopify-section-cart-icon-bubble') || newDoc.querySelector('.shopify-section');
//         const oldBubble = document.querySelector('#shopify-section-cart-icon-bubble');
//         if (newBubble && oldBubble) oldBubble.innerHTML = newBubble.innerHTML;
//       }
//       document.dispatchEvent(new CustomEvent('cart:updated'));
//     } catch (e) { console.warn('[FreeGifts] rerender failed', e); }
//   }

//   /* ---------------- fetch hook ---------------- */

//   function installFetchHook() {
//     const origFetch = window.fetch;
//     let reconciling = false;

//     window.fetch = async function (...args) {
//       const response = await origFetch.apply(this, args);
//       try {
//         const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || '';
//         const isCartMutation = /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
//         if (isCartMutation && !reconciling) {
//           const init = args[1] || {};
//           const body = init.body || '';
//           const isInternal = (typeof body === 'string' && (body.includes('"_free_gift"') || body.includes('"_tier_gift"')));
//           if (!isInternal) {
//             reconciling = true;
//             const responseClone = response.clone();
//             // Theme components (quantity steppers, product forms) bundle a
//             // `sections` render into their own mutation and update the drawer
//             // themselves — our extra render would be a wasted request. Only
//             // render for sources that DON'T (third-party apps, raw calls).
//             const sourceRendersSections = (typeof body === 'string' && body.includes('"sections"'));
//             setTimeout(async () => {
//               try {
//                 // 1) Reflect the customer's action right away — in PARALLEL
//                 //    with the gift computation below — but only when the
//                 //    mutation source didn't already render the drawer.
//                 const firstRender = sourceRendersSections ? Promise.resolve() : rerenderDrawer();
//                 // 2) Get the cart WITHOUT an extra round-trip when possible.
//                 const cart = (await cartFromMutation(url, responseClone)) || (await fetchCart());
//                 // 3) One reconciliation pass, max two batched requests.
//                 const changed = await syncGifts(cart);
//                 await firstRender;
//                 // 4) Gifts changed → one more render so they appear.
//                 if (changed) await rerenderDrawer();
//               } catch (e) { console.warn('[FreeGifts] reconcile sequence failed', e); }
//               reconciling = false;
//             }, 10);
//           }
//         }
//       } catch (e) { /* never break the original fetch */ }
//       return response;
//     };
//   }

//   /* ---------------- init ---------------- */

//   async function init() {
//     installFetchHook();
//     try {
//       // Sync on load: carts built on a previous visit, or thresholds changed
//       // in the customizer, get corrected immediately.
//       const changed = await syncGifts(null);
//       if (changed) await rerenderDrawer();
//     } catch (e) { console.warn('[FreeGifts] init reconcile failed', e); }
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', init);
//   } else {
//     init();
//   }
// })();